import { useState, useEffect } from 'react';
import './App.css';
import 'popper.js';
import 'bootstrap';
import 'bootstrap-select';
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-select/dist/css/bootstrap-select.css';
import moment from 'moment-timezone';
import { v4 as uuidv4 } from 'uuid'; // Import the UUID function
import ResetConfirmationModal from './components/ResetConfirmationModal';
import AddEditTimezoneModal from './components/AddEditTimezoneModal';
import Header from './components/Header';
import MainContent from './components/MainContent';
import ActionButtons from './components/ActionButtons';

const local_timezone = moment.tz.guess();

interface Timezone {
  display_name: string;
  tz_string: string;
  id: string;
}

// Function to generate unique IDs using UUID
const generateId = () => uuidv4();

const default_timezones: Timezone[] = [
  { display_name: 'Seattle, USA', tz_string: 'America/Los_Angeles', id: generateId() },
  { display_name: 'New Orleans, USA', tz_string: 'America/Chicago', id: generateId() },
  { display_name: '🗽', tz_string: 'America/New_York', id: generateId() },
  { display_name: 'Toulouse Space Centre', tz_string: 'Europe/Paris', id: generateId() },
];

const timezoneOptions = moment.tz.names().map((tz) => ({
  value: tz,
  label: tz,
}));

function App() {
  const [is24hFormat, setIs24hFormat] = useState(() => {
    const storedFormat = localStorage.getItem('is24hFormat');
    return storedFormat ? JSON.parse(storedFormat) : false; // Default to 12-hour mode
  });
  const [currentTime, setCurrentTime] = useState(moment());
  const [timezones, setTimezones] = useState<Timezone[]>(() => {
    const storedTimezones = localStorage.getItem('timezones');
    return storedTimezones ? JSON.parse(storedTimezones) : default_timezones;
  });
  const [showAddEditForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState<'add' | 'edit'>('add');
  const [editingTimezone, setEditingTimezone] = useState<Timezone | null>(null);
  const [showResetModal, setShowResetModal] = useState(false); // State for reset confirmation modal

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(moment());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    localStorage.setItem('timezones', JSON.stringify(timezones));
  }, [timezones]);

  useEffect(() => {
    localStorage.setItem('is24hFormat', JSON.stringify(is24hFormat));
  }, [is24hFormat]);

  const handleAddTimezone = (timezone: { value: string; label: string }, displayName: string) => {
    const newId = generateId(); // Generate a unique ID for the new timezone
    setTimezones([
      ...timezones,
      { display_name: displayName, tz_string: timezone.value, id: newId },
    ]);
    setShowForm(false);
  };

  const handleEditTimezone = (timezone: { value: string; label: string }, displayName: string) => {
    if (editingTimezone) {
      setTimezones(
        timezones.map((tz) =>
          tz.id === editingTimezone.id
            ? { ...tz, display_name: displayName, tz_string: timezone.value }
            : tz
        )
      );
      setEditingTimezone(null);
      setShowForm(false);
    }
  };

  const removeTimezone = (id: string) => {
    setTimezones(timezones.filter((timezone: Timezone) => timezone.id !== id));
  };

  const handleResetToDefault = () => {
    setTimezones(default_timezones);
    localStorage.removeItem('timezones');
    setShowResetModal(false); // Close the modal after resetting
  };

  const handleReorderTimezones = (reorderedTimezones: Timezone[]) => {
    setTimezones(reorderedTimezones); // Update the state with the new order
  };

  // Custom filter function for react-select
  const filterTimezoneOptions = (option: { value: string; label: string }, inputValue: string) => {
    const normalizedOption = option.label.replace(/[_/]/g, ' ').toLowerCase();
    const normalizedInput = inputValue.replace(/[_/]/g, ' ').toLowerCase();
    return normalizedOption.includes(normalizedInput);
  };

  return (
    <main role="main">
      <div className="container">
        <Header is24hFormat={is24hFormat} onClockFormatChange={() => setIs24hFormat(!is24hFormat)} />
        <MainContent
          timezones={timezones}
          local_timezone={local_timezone}
          is24hFormat={is24hFormat}
          currentTime={currentTime}
          onRemoveTimezone={removeTimezone}
          onEditTimezone={(timezone: Timezone) => {
            setEditingTimezone(timezone);
            setFormMode('edit');
            setShowForm(true);
          }}
          onReorderTimezones={handleReorderTimezones}
        />
        <ActionButtons
          hasTimezones={timezones.length > 0}
          onAddTimezone={() => {
            setFormMode('add');
            setShowForm(true);
          }}
          onReset={() => setShowResetModal(true)}
        />
      </div>

      {showResetModal && (
        <ResetConfirmationModal
          onConfirm={handleResetToDefault}
          onCancel={() => setShowResetModal(false)}
        />
      )}

      {showAddEditForm && (
        <AddEditTimezoneModal
          formMode={formMode}
          editingTimezone={
            formMode === 'edit' && editingTimezone
              ? { value: editingTimezone.tz_string, label: editingTimezone.tz_string }
              : null
          }
          initialDisplayName={formMode === 'edit' && editingTimezone ? editingTimezone.display_name : ''}
          timezoneOptions={timezoneOptions}
          onSubmit={formMode === 'add' ? handleAddTimezone : handleEditTimezone}
          onCancel={() => {
            setShowForm(false);
            setEditingTimezone(null);
          }}
          filterOption={filterTimezoneOptions}
        />
      )}
    </main>
  );
}

export default App;
